import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { useTranslation } from 'react-i18next';
import { MessageCircle } from 'lucide-react';
import { useChat } from '../../hooks/useChat';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import ChatHeader from './ChatHeader';
import ChatLoader from './ChatLoader';
import classNames from 'classnames';

/**
 * Componente principal del chat
 * @param {Object} props - Propiedades del componente
 * @param {string} props.mode - Modo del chat ('ia' | 'interno')
 * @param {string} props.userId - ID del usuario
 * @param {string} props.endpoint - Endpoint para las peticiones
 * @param {string} props.theme - Tema del chat
 * @param {boolean} props.floating - Si el chat debe ser flotante
 * @param {string} props.position - Posición del chat flotante
 * @param {boolean} props.disabled - Deshabilitar el chat
 * @param {Function} props.onClose - Callback cuando se cierra el chat
 * @param {Function} props.onMessage - Callback cuando se recibe un mensaje
 */
const Chat = ({
  mode = 'ia',
  userId,
  endpoint = 'https://www.ciacosinte.site/cia2_google/JSON/CriterIA/chat_client.php',
  theme = 'default',
  floating = true,
  position = 'bottom-right',
  disabled = false,
  onClose,
  onMessage,
  ...props
}) => {
  const { t } = useTranslation();
  const [minimized, setMinimized] = useState(false);
  
  // Configuración del chat - usamos useMemo para evitar recrear el objeto en cada render
  const chatConfig = useMemo(() => ({
    mode,
    userId,
    endpoint,
    theme
  }), [mode, userId, endpoint, theme]);

  // Hook del chat
  const {
    messages,
    isLoading,
    error,
    isOpen,
    unreadCount,
    sendMessage,
    toggleChat,
    clearError,
    clearChat
  } = useChat(chatConfig);

  // Callback cuando se recibe un mensaje - solo cuando hay un nuevo mensaje
  useEffect(() => {
    if (onMessage && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      // Solo ejecutar si es realmente un mensaje nuevo
      if (lastMessage && lastMessage.timestamp) {
        const messageTime = new Date(lastMessage.timestamp).getTime();
        const now = Date.now();
        // Solo si el mensaje es de los últimos 5 segundos (mensaje nuevo)
        if (now - messageTime < 5000) {
          onMessage(lastMessage);
        }
      }
    }
  }, [messages.length, onMessage]);

  // Manejar envío de mensaje
  const handleSendMessage = useCallback(async (message) => {
    await sendMessage(message);
  }, [sendMessage]);

  // Manejar cierre del chat
  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    } else {
      toggleChat();
    }
  }, [onClose, toggleChat]);

  // Manejar minimizar
  const handleMinimize = useCallback(() => {
    setMinimized(!minimized);
  }, [minimized]);

  // Renderizar botón flotante
  const renderFloatingButton = () => {
    if (!floating || isOpen) return null;

    return (
      <div className={`chat-floating-button chat-floating-button--${position}`}>
        <Button
          icon={<MessageCircle size={20} />}
          rounded
          className="chat-toggle-button"
          onClick={toggleChat}
          tooltip={t('chat.abrir_chat')}
          tooltipOptions={{ position: 'left' }}
        >
          {unreadCount > 0 && (
            <Badge value={unreadCount} severity="danger" className="chat-badge" />
          )}
        </Button>
      </div>
    );
  };

  // Renderizar ventana del chat
  const renderChatWindow = () => {
    if (!isOpen) return null;

    const chatClasses = classNames('chat-container', {
      'chat-container--floating': floating,
      'chat-container--minimized': minimized,
      [`chat-container--${theme}`]: theme,
      [`chat-container--${position}`]: floating
    });

    const content = (
      <div className={chatClasses}>
        <ChatHeader
          mode={mode}
          onClose={handleClose}
          onMinimize={handleMinimize}
          onClearChat={clearChat}
          minimized={minimized}
        />
        
        {!minimized && (
          <>
            <div className="chat-messages">
              {error && (
                <div className="chat-error">
                  <div className="alert alert-danger">
                    <span>{error}</span>
                    <Button
                      icon="pi pi-times"
                      className="p-button-text p-button-sm"
                      onClick={clearError}
                    />
                  </div>
                </div>
              )}
              
              {messages.length === 0 && !isLoading && (
                <div className="chat-empty">
                  <div className="chat-empty-content">
                    <MessageCircle className="chat-empty-icon" size={48} />
                    <p>{t('chat.sin_mensajes')}</p>
                    <p className="chat-empty-subtitle">
                      {mode === 'ia' 
                        ? t('chat.inicia_conversacion_ia') 
                        : t('chat.inicia_conversacion_usuarios')
                      }
                    </p>
                  </div>
                </div>
              )}

              {messages.map((message) => (
                <ChatMessage key={message.id} message={message} />
              ))}

              {isLoading && <ChatLoader />}
            </div>

            <ChatInput
              onSendMessage={handleSendMessage}
              disabled={isLoading}
              placeholder={
                mode === 'ia' 
                  ? t('chat.escribe_mensaje_ia') 
                  : t('chat.escribe_mensaje_usuarios')
              }
            />
          </>
        )}
      </div>
    );

    if (floating) {
      return content;
    }

    return (
      <Dialog
        header={t('chat.titulo')}
        visible={isOpen}
        style={{ width: '50vw', minWidth: '400px' }}
        onHide={handleClose}
        maximizable
        modal={false}
        className="chat-dialog"
      >
        {content}
      </Dialog>
    );
  };

  // Si el chat está deshabilitado, no renderizar nada
  if (disabled) {
    return null;
  }

  return (
    <>
      <ConfirmDialog />
      {renderFloatingButton()}
      {renderChatWindow()}
    </>
  );
};

export default Chat;
